import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectFlaggedExactBars, setFlaggedExactBars } from "../ui/uiSlice";
import { selectProject } from "./projectSlice";
import { failingExactBars } from "./exactBarChecks";

const NO_MARKS: ReadonlyMap<string, string[]> = new Map();

/**
 * The hand-placed bars to mark as wrong, by guid, each with the reasons it fails.
 *
 * A bar is marked when it was flagged by the last refused calculation *and* still fails now: fixing it
 * removes the mark straight away, without waiting for the next press, while an edit that breaks some
 * other bar marks nothing until then (see exactBarChecks for why). A flagged bar that passes is also
 * dropped from the flagged set, so the marks only ever shrink — breaking the same bar again does not
 * bring its mark back.
 *
 * Flags that no longer match any bar (another project, or exact mode switched off and on, which makes
 * new bars) simply match nothing, so nothing has to clear them.
 */
export const useExactBarMarks = (): ReadonlyMap<string, string[]> => {
  const dispatch = useAppDispatch();
  const flagged = useAppSelector(selectFlaggedExactBars);
  const input = useAppSelector(selectProject)?.data?.data?.input;

  // Until a calculation has been refused there is nothing to mark, so the check is not run at all.
  const anyFlagged = flagged.length > 0;
  const failing = useMemo(() => (anyFlagged ? failingExactBars(input) : NO_MARKS), [anyFlagged, input]);

  const stillFlagged = useMemo(() => flagged.filter((guid) => failing.has(guid)), [flagged, failing]);

  useEffect(() => {
    if (stillFlagged.length !== flagged.length) {
      dispatch(setFlaggedExactBars(stillFlagged));
    }
  }, [dispatch, flagged.length, stillFlagged]);

  return useMemo(() => {
    if (stillFlagged.length === 0) {
      return NO_MARKS;
    }
    return new Map(stillFlagged.map((guid) => [guid, failing.get(guid) ?? []]));
  }, [failing, stillFlagged]);
};
